import { ChangeDetectionStrategy, Component } from '@angular/core';
import { MoveSandbox } from '@app/move/sandboxes/move.sandbox';
import { Navigation } from '@app/navigation/enums/navigation.enum';
import { ServicesSandbox } from '@app/services/sandboxes/services.sandbox';
import { ServiceType } from '@app/services/types/service-type';
import { PosthogServicesService } from '@app/tracking/posthog-services.service';
import { DbUtils } from '@core/utils';
import { ServiceCardItem } from '@ui/cards';
import { UiContext } from '@ui/ui.enums';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
    selector: 'app-major-service-list',
    template: `
        <h1 *ngIf="showAnyCard$ | async" class="u-margin-bottom u-font-size-28" id="title">{{ 'DASHBOARD.MAJOR_SERVICE_LIST.TITLE' | translate }}</h1>

        <smvd-ui-service-card
            *ngIf="showEnergy$ | async"
            [title]="'DASHBOARD.ENERGY.TITLE' | translate"
            [description]="'DASHBOARD.ENERGY.TEXT' | translate"
            [service]="service.Energy"
            class="u-margin-bottom"
            id="energy-card"
        >
            <ng-container image-content>
                <img [ngSrc]="'https://assets.smooved.be/images/service_energy.png'" width="72" height="121" />
            </ng-container>
            <ng-container actions-content>
                <a
                    (click)="posthogServicesService.clickService(serviceType.EnergyNewContract)"
                    smvd-ui-button
                    [context]="uiContext.PrimaryDark"
                    [routerLink]="energyLink"
                    class="u-w-fit-content"
                    >{{ 'DASHBOARD.ENERGY.OFFER' | translate }}</a
                >
            </ng-container>
        </smvd-ui-service-card>

        <smvd-ui-service-card
            *ngIf="showBoilerMaintenance$ | async"
            [title]="'DASHBOARD.BOILER_MAINTENANCE.TITLE' | translate"
            [description]="'DASHBOARD.BOILER_MAINTENANCE.TEXT' | translate"
            [service]="service.BoilerMaintenance"
            class="u-margin-bottom"
            id="boiler-maintenance-card"
        >
            <ng-container image-content>
                <img [ngSrc]="'https://assets.smooved.be/images/service_energy.png'" width="72" height="121" />
            </ng-container>
            <ng-container actions-content>
                <a
                    (click)="posthogServicesService.clickService(serviceType.BoilerMaintenance)"
                    smvd-ui-button
                    [context]="uiContext.PrimaryDark"
                    [routerLink]="boilerLink"
                    class="u-w-fit-content"
                    >{{ 'DASHBOARD.BOILER_MAINTENANCE.OFFER' | translate }}</a
                >
            </ng-container>
        </smvd-ui-service-card>

        <smvd-ui-service-card
            *ngIf="showTelecom$ | async"
            [title]="'DASHBOARD.TELECOM.TITLE' | translate"
            [description]="'DASHBOARD.TELECOM.TEXT' | translate"
            [service]="service.Telecom"
            class="u-margin-bottom"
            id="telecom-card"
        >
            <ng-container image-content>
                <img [ngSrc]="'https://assets.smooved.be/images/service_telecom.png'" width="94" height="93" />
            </ng-container>
            <ng-container actions-content>
                <a
                    (click)="posthogServicesService.clickService(serviceType.TelecomNewContract)"
                    smvd-ui-button
                    [context]="uiContext.PrimaryDark"
                    [routerLink]="telecomLink"
                    class="u-w-fit-content"
                    >{{ 'DASHBOARD.TELECOM.OFFER' | translate }}</a
                >
            </ng-container>
        </smvd-ui-service-card>

        <smvd-ui-service-card
            *ngIf="showTelecom$ | async"
            [title]="'DASHBOARD.TELECOM.TRANSFER.TITLE' | translate"
            [description]="'DASHBOARD.TELECOM.TRANSFER.TEXT' | translate"
            [service]="service.TelecomTransfer"
            class="u-margin-bottom"
            id="telecom-transfer-card"
        >
            <ng-container image-content>
                <img [ngSrc]="'https://assets.smooved.be/images/service_telecom.png'" width="94" height="93" />
            </ng-container>
            <ng-container actions-content>
                <a
                    (click)="posthogServicesService.clickService(serviceType.TelecomExistingContract)"
                    smvd-ui-button
                    [context]="uiContext.PrimaryDark"
                    [routerLink]="telecomTransferLink"
                    class="u-w-fit-content"
                    >{{ 'DASHBOARD.TELECOM.TRANSFER.CTA' | translate }}</a
                >
            </ng-container>
        </smvd-ui-service-card>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MajorServiceListComponent {
    public readonly uiContext = UiContext;
    public readonly service = ServiceCardItem;
    public readonly serviceType = ServiceType;

    public energyLink = `/${Navigation.Energy}`;
    public boilerLink = `/${Navigation.Services}/${Navigation.BoilerMaintenance}`;
    public telecomLink = `/${Navigation.Telecom}`;
    public telecomTransferLink = `/${Navigation.TelecomTransfer}`;

    protected readonly DbUtils = DbUtils;

    public showEnergy$ = combineLatest([this.moveSandbox.energyOfferSelected$, this.moveSandbox.energyOrdered$]).pipe(
        map(([energyOfferSelected, energyOrdered]) => {
            if (energyOrdered) return false;
            return !energyOfferSelected;
        })
    );

    public showBoilerMaintenance$ = combineLatest([
        this.servicesSandbox.orderedBoilerMaintenanceContract$,
        this.moveSandbox.isLeaver$,
    ]).pipe(
        map(([bmContract, isLeaver]) => {
            return !DbUtils.getStringId(bmContract) && !isLeaver;
        })
    );

    public showTelecom$ = combineLatest([this.moveSandbox.telecomOfferOrTransferSelected$, this.moveSandbox.telecomOrdered$]).pipe(
        map(([telecomOfferOrTransferSelected, telecomOrdered]) => {
            if (telecomOrdered) return false;
            return !telecomOfferOrTransferSelected;
        })
    );

    public showAnyCard$ = combineLatest([
        this.showEnergy$,
        this.showBoilerMaintenance$,
        this.showTelecom$
    ]).pipe(
        map(([energy, boiler, telecom]) => {
            const result = energy || boiler || telecom;
            return result;
        })
    );

    constructor(
        public readonly moveSandbox: MoveSandbox,
        public readonly posthogServicesService: PosthogServicesService,
        public readonly servicesSandbox: ServicesSandbox
    ) {}
}
